/**
 * Which provider collections the timeline should fetch alongside the database.
 *
 * Lives here rather than in `DBRecordsLoader` because it needs the collection
 * list, and the loader deliberately touches neither Redux nor RTK Query. The
 * loader is handed the finished list and compares it by value, so returning a
 * fresh array every render costs nothing.
 */

import { useMemo } from "react";

import { useCollections, useIsOwnSpace } from "@/api/hooks";
import { buildDescendantsMap } from "@/common/utils";

import { showsInAllCollections } from "./registry";
import { ExternalSource } from "./source";

export function useExternalSources(
  collectionID: string | undefined,
): ExternalSource[] {
  const collections = useCollections();
  const isOwnSpace = useIsOwnSpace();

  return useMemo(() => {
    // Fetching happens with the viewer's own grant, so someone else's provider
    // collection is empty by construction rather than forbidden -- there is no
    // token that could fill it.
    if (!isOwnSpace) return [];

    const providers = collections.filter((c) => c.provider_key);
    if (providers.length === 0) return [];

    // A provider root that has natural subcollections never fetches for itself.
    // Its children partition the same account -- Gmail's root query is "all
    // mail", and its children are slices of that -- so letting both run would
    // list every message twice, once under each. The root is the container and
    // the holder of the connection; the leaves are what read.
    const rootsWithChildren = new Set(
      providers
        .map((c) => c.provider_root_id)
        .filter((id): id is string => !!id),
    );
    let inScope = providers.filter((c) => !rootsWithChildren.has(c.id));

    if (!collectionID) {
      // The all-collections timeline. A provider stays out of it unless its
      // root says otherwise: a mailbox is dense enough to bury a space's own
      // records, and "everything I have written" is the question this page
      // answers. The opt-in lives on the root because that is the unit a
      // person thinks in -- Gmail, not Gmail/Promotions.
      const byId = new Map(collections.map((c) => [c.id, c]));
      inScope = inScope.filter((c) => {
        const root = c.provider_root_id
          ? (byId.get(c.provider_root_id) ?? c)
          : c;
        return showsInAllCollections(root.provider_config);
      });
    }

    if (collectionID) {
      // The record query scopes to a collection and everything under it; the
      // external sources have to agree, or opening a collection would show its
      // records but not its provider subcollections.
      const descendantsOf = buildDescendantsMap(collections);
      const allowed = descendantsOf.get(collectionID) ?? new Set<string>();
      inScope = inScope.filter(
        (c) => c.id === collectionID || allowed.has(c.id),
      );
    }

    return inScope.map((collection) => ({
      collectionId: collection.id,
      providerKey: collection.provider_key as string,
      config: collection.provider_config ?? {},
      connectionId: collection.connection_id ?? null,
    }));
  }, [collections, collectionID, isOwnSpace]);
}
